import { AppIdentifier, FrameLanguage } from '@aipb/gulliver/env/model';
import { EnvService } from '@aipb/gulliver/env/util';
import { FrameRepository } from '@aipb/gulliver/frame/data-access';
import { PersonalContentPermissionService } from '@aipb/gulliver/personal-content/auth';
import { AuthService } from '@aipb/shared/auth';
import { setTranslationLang } from '@aipb/shared/util/other';
import { OverlayManagerComponent, OverlayService } from '@aipb/shared/util/overlay';
import { openUrl } from '@aipb/shared/util/routing';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Output, TemplateRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApplicationMenuDialogService } from '../../../application-menu-dialog';
import { CoopMessagesDialogService } from '../../../coop-messages-dialog';
import { CoopMessagesComponent } from '../../../coop-messages/coop-messages.component';
import {
  CoopMessagesButtonComponent,
  LanguageButtonComponent,
  LoginButtonComponent,
  LogoComponent,
  LogoutButtonComponent,
  OverlayManagerButtonComponent,
  PersonalContentButtonComponent,
  PersonalContentPluginButtonComponent
} from '../../components';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'fr-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    LogoutButtonComponent,
    LoginButtonComponent,
    LanguageButtonComponent,
    PersonalContentButtonComponent,
    PersonalContentPluginButtonComponent,
    LogoComponent,
    MatButtonModule,
    OverlayManagerButtonComponent,
    CoopMessagesButtonComponent,
    CoopMessagesComponent,
    MatIcon,
    TranslocoPipe
  ],
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss']
})
export class ToolbarComponent {
  @Output() togglePersonalContentPlugin = new EventEmitter();

  env = inject(EnvService);
  applicationMenuDialogService = inject(ApplicationMenuDialogService);
  auth = inject(AuthService);
  transloco = inject(TranslocoService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  personalContentPermission = inject(PersonalContentPermissionService);
  frameRepository = inject(FrameRepository);
  showBetaTag = this.env.raw.frame.showBetaTag || false;
  titleLogoMode = this.env.raw.frame.title_logo_mode || 'logo_left';
  title = this.env.raw.frame.title;
  logo = this.env.raw.frame.logo;
  logos = this.env.raw.frame.logos;
  instructionsUrl = this.env.raw.frame.instructionsUrl;
  languages = this.env.raw.frame.languages;
  isLoggedIn$ = this.auth.isLoggedIn$();
  currentLanguage$ = this.transloco.langChanges$.pipe(
    startWith(this.transloco.getActiveLang()),
    map(() => this.transloco.getActiveLang())
  );
  username$ = this.auth.isLoggedIn$().pipe(map(() => this.auth.getUsername()));
  hasMenu$ = this.frameRepository.applications$.pipe(
    map(
      (applications) =>
        applications.filter(
          (app) =>
            !app.hideInMenu &&
            app.id in this.auth.appRequiredRoles &&
            this.auth.appRequiredRoles[app.id].every((role) => this.auth.hasRole(role))
        ).length > 1
    )
  );
  private coopMessagesDialog = inject(CoopMessagesDialogService);
  private overlayService = inject(OverlayService);

  get currentLang() {
    return this.transloco.getActiveLang();
  }

  get hasPersonalContent() {
    return this.hasApplication('personal-content');
  }

  get isPersonalContentEnabled(): boolean {
    if (!this.env) return false;

    return this.env.isPersonalContentEnabled && this.personalContentPermission.canUsePersonalContent;
  }

  onOpenAppMenu(): void {
    this.applicationMenuDialogService.open({
      apps: this.frameRepository.applications
    });
  }

  onInstruction(url: string): void {
    openUrl(url);
  }

  onLogin(): void {
    this.auth.login();
  }

  onLogout(): void {
    this.auth.logout();
  }

  onChangeLanguage(language: FrameLanguage): void {
    this.transloco.setActiveLang(language.name);
    setTranslationLang(language.name, this.env.name);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        lang: language.name
      },
      queryParamsHandling: 'merge'
    });
  }

  onOpenOverlayManager() {
    this.overlayService.open(OverlayManagerComponent, {}, 'overlay.overlay_manager');
  }

  onOpenOverlay(templateRef: TemplateRef<any>) {
    this.overlayService.open(templateRef, {}, this.transloco.translate('personal_content.routes.coop_default'));
  }

  private hasApplication(app: AppIdentifier): boolean {
    return app in this.env.raw.applications;
  }
}
